import { useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { Refresh } from "@mui/icons-material";
import {
  formatHeatmapTimestamp,
  formatHeatmapValue,
  getHeatmapDate,
  getHeatmapGradient,
  getHeatmapHourOptions,
  getLastCompleteHeatmapHour,
  HEATMAP_MEASURES,
  HEATMAP_TIME_ZONE,
} from "../../utils/sensorHeatmapUtils";

const inputStyle = {
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  padding: "6px",
  border: "1px solid rgba(0,0,0,0.2)",
  borderRadius: "4px",
  fontSize: "12px",
  background: "white",
  color: "#333",
};

const SensorHeatmapControlContent = ({ heatmap, opacity, onOpacityChange }) => {
  const [measurementName, setMeasurementName] = useState("temperature");
  const [timestamp, setTimestamp] = useState(getLastCompleteHeatmapHour);
  const [date, setDate] = useState(() => getHeatmapDate(timestamp));
  const hours = getHeatmapHourOptions(date);
  const validHour = hours.some((option) => option.value === timestamp);
  const isLoading = heatmap.status === "loading";
  const data = heatmap.status === "ready" ? heatmap.data : null;
  const measure =
    data &&
    HEATMAP_MEASURES.find((item) => item.apiKey === data.measurement_name);
  const gradient = data && getHeatmapGradient(data.colormap_name);

  // Fetch only for user actions; status/data/callback updates must never refetch.
  // The field page already requests the initial selection when heatmap is enabled.
  const requestHeatmap = (nextMeasurementName, nextTimestamp, nextDate = date) => {
    if (
      !heatmap.enabled ||
      heatmap.disabledReason ||
      !getHeatmapHourOptions(nextDate).some(
        (option) => option.value === nextTimestamp,
      )
    ) {
      return;
    }

    heatmap.onApply?.({
      measurementName: nextMeasurementName,
      timestamp: nextTimestamp,
    });
  };

  return (
    <Box display="flex" flexDirection="column" gap={1} width="100%">
      <label>
        Misura
        <select
          aria-label="Misura heatmap"
          value={measurementName}
          style={inputStyle}
          onChange={(event) => {
            const nextMeasurementName = event.target.value;
            if (nextMeasurementName === measurementName) return;
            setMeasurementName(nextMeasurementName);
            requestHeatmap(nextMeasurementName, timestamp);
          }}
        >
          {HEATMAP_MEASURES.map((item) => (
            <option key={item.apiKey} value={item.apiKey}>
              {item.label}
            </option>
          ))}
        </select>
      </label>
      <Box display="flex" gap={1}>
        <label style={{ flex: 1, minWidth: 0 }}>
          Data
          <input
            type="date"
            aria-label="Data heatmap"
            style={inputStyle}
            value={date}
            max={getHeatmapDate(getLastCompleteHeatmapHour())}
            onChange={(event) => {
              const nextDate = event.target.value;
              if (nextDate === date) return;
              const options = getHeatmapHourOptions(nextDate);
              const nextTimestamp = options[options.length - 1]?.value || "";
              setDate(nextDate);
              setTimestamp(nextTimestamp);
              requestHeatmap(measurementName, nextTimestamp, nextDate);
            }}
          />
        </label>
        <label style={{ flex: 1, minWidth: 0 }}>
          Ora
          <select
            aria-label="Ora heatmap"
            style={inputStyle}
            value={timestamp}
            onChange={(event) => {
              const nextTimestamp = event.target.value;
              if (nextTimestamp === timestamp) return;
              setTimestamp(nextTimestamp);
              requestHeatmap(measurementName, nextTimestamp);
            }}
          >
            {hours.length === 0 && (
              <option value="">Nessuna ora disponibile</option>
            )}
            {hours.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
      >
        <Typography fontSize="11px" color="text.secondary">
          Fuso: {HEATMAP_TIME_ZONE}
        </Typography>
        <Tooltip title="Ricarica heatmap">
          <span>
            <IconButton
              size="small"
              color="primary"
              aria-label="Ricarica heatmap"
              aria-busy={isLoading}
              disabled={
                !validHour || isLoading || !heatmap.onApply || Boolean(heatmap.disabledReason)
              }
              onClick={() => requestHeatmap(measurementName, timestamp)}
              sx={{ width: 40, height: 40 }}
            >
              {isLoading ? (
                <CircularProgress size={18} color="inherit" aria-hidden="true" />
              ) : (
                <Refresh fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      {isLoading && (
        <Typography role="status" fontSize="12px">
          Caricamento heatmap…
        </Typography>
      )}
      {(heatmap.status === "empty" || heatmap.status === "error") && (
        <Typography
          role={heatmap.status === "error" ? "alert" : "status"}
          fontSize="12px"
          color={heatmap.status === "error" ? "error" : "text.secondary"}
        >
          {heatmap.error}
        </Typography>
      )}
      {data && (
        <Box
          aria-label="Legenda heatmap"
          display="flex"
          flexDirection="column"
          gap={0.5}
        >
          <Typography fontWeight="bold" fontSize="12px">
            {measure?.label} ({measure?.unit})
          </Typography>
          <Typography fontSize="11px">
            {formatHeatmapTimestamp(data.start_timestamp)}
          </Typography>
          {gradient && (
            <Box height={12} borderRadius="2px" sx={{ background: gradient }} />
          )}
          <Box display="flex" justifyContent="space-between" fontSize="12px">
            <span>{formatHeatmapValue(data.valueRange[0])}</span>
            <span>{formatHeatmapValue(data.valueRange[1])}</span>
          </Box>
          <Typography fontSize="11px" color="text.secondary">
            Scala relativa all’ora visualizzata · {data.colormap_name}
          </Typography>
        </Box>
      )}
      <label>
        Opacità: {Math.round(opacity * 100)}%
        <input
          type="range"
          aria-label="Opacità heatmap"
          min="0"
          max="100"
          step="5"
          value={Math.round(opacity * 100)}
          style={{ width: "100%", accentColor: "#216557" }}
          onChange={(event) =>
            onOpacityChange(Number(event.target.value) / 100)
          }
        />
      </label>
    </Box>
  );
};

export default SensorHeatmapControlContent;
